import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ServiceMessageSettings = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    message: '',
    start_time: '',
    end_time: '',
    enabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE}/api/superadmin/service-message/settings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Convert ISO strings to datetime-local format
      const data = response.data;
      setSettings({
        message: data.message || '',
        start_time: data.start_time ? new Date(data.start_time).toISOString().slice(0, 16) : '',
        end_time: data.end_time ? new Date(data.end_time).toISOString().slice(0, 16) : '',
        enabled: data.enabled || false
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching service message settings:', error);
      toast.error('Kunne ikke laste servicemeldingsinnstillinger');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!settings.message.trim()) {
      toast.error('Meldingstekst kan ikke være tom');
      return;
    }
    
    if (!settings.start_time || !settings.end_time) {
      toast.error('Start- og sluttid må fylles ut');
      return;
    }
    
    const startDate = new Date(settings.start_time);
    const endDate = new Date(settings.end_time);
    
    if (endDate <= startDate) {
      toast.error('Sluttid må være etter starttid');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_BASE}/api/superadmin/service-message/settings`,
        {
          message: settings.message,
          start_time: new Date(settings.start_time).toISOString(),
          end_time: new Date(settings.end_time).toISOString(),
          enabled: settings.enabled
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Servicemeldingsinnstillinger lagret!');
    } catch (error) {
      console.error('Error saving service message settings:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke lagre innstillinger';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg">
        <p className="text-gray-400">Laster...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-6 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Servicemelding</h3>
          <div className="flex items-center space-x-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                className="rounded border-gray-600"
              />
              <span className="text-sm text-gray-300">
                {settings.enabled ? (
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    Aktivert
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <EyeOff className="h-4 w-4" />
                    Deaktivert
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>

        <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg flex gap-2">
          <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200">
            <p className="font-medium mb-1">Om servicemeldinger:</p>
            <p>Meldingen vises som en stripe-toast øverst på login-siden for alle brukere (medlemmer og skyteledere) i det tidsvinduet du setter.</p>
            <p className="mt-2">Brukere kan lukke meldingen manuelt, eller den forsvinner automatisk etter 15 sekunder.</p>
          </div>
        </div>

        {/* Message Text */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Meldingstekst
          </label>
          <textarea
            value={settings.message}
            onChange={(e) => setSettings({ ...settings, message: e.target.value })}
            placeholder="F.eks: Planlagt vedlikehold 15. november kl 02:00-04:00. Systemet kan være utilgjengelig i denne perioden."
            rows={3}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">
            Denne meldingen vil vises for alle brukere på login-siden
          </p>
        </div>

        {/* Start Time */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Starttid
          </label>
          <input
            type="datetime-local"
            value={settings.start_time}
            onChange={(e) => setSettings({ ...settings, start_time: e.target.value })}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">
            Når skal meldingen begynne å vises?
          </p>
        </div>

        {/* End Time */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Sluttid
          </label>
          <input
            type="datetime-local"
            value={settings.end_time}
            onChange={(e) => setSettings({ ...settings, end_time: e.target.value })}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">
            Når skal meldingen slutte å vises?
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center justify-center gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Lagrer...' : 'Lagre innstillinger'}
        </button>
      </div>
    </div>
  );
};

export default ServiceMessageSettings;
